import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { v4 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import Table from '../../Table/Table';
import TableHead from '../../Table/TableHead';
import TableBody from '../../Table/TableBody';
import TableRow from '../../Table/TableRow';
import TransactionRow from '../../TransactionTable/TransactionRow';
import { formatDate } from '../../../util/helpers/dateHelpers';
import {
  emptyPortfolioTransactionSingle,
  portfolioTransactionsSingleFetch,
} from '../../../store/actions/portfolio/portfolioActions';
import TransactionDetails from '../../Modals/Transaction/TransactionDetails';
import Modal from '../../Modal/Modal';
import SellFromHomeTransactionDetails from '../../Modals/SellWizard/SellFromHomeTransactionDetails';
import SellFromHomeTransactionHistory from '../../Modals/SellWizard/SellFromHomeTransactionHistory';
import SfhModalTitle from '../../Modals/SellWizard/SfhModalTitle';
import TriggerOrderDetailsModal from '../../Modals/TriggerOrderDetails/TriggerOrderDetailsModal';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../../store/selectors/portfolioTransactionsSelector';
import { getHeaderData, equityHeaderData } from './headerData';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { selectIsPriceAlertEligibleAccount } from '../../../store/selectors/accountSelectors';
import config from '../../../config';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import {
  transactionStatusTypesArray,
  transactionTypes,
  SELL_FROM_HOME,
} from '../../../util/enum/api/transactionTypes';
import { pxToRem } from '../../../assets/styles/helper';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import {
  getSingleTransactionType,
  isTargetPriceOrderTransaction,
} from '../../../util/helpers/transcation/singleTransactionHelper';
import {
  asTargetPriceOrderSummary,
  transactionFieldFormatter,
} from '../../../util/helpers/transcation/transactionHelper';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import SectionLoader from '../../Loader/SectionLoader';
import useCheckPermissions from '../../../util/hooks/useCheckPermissions';
import {
  balanceTransactionTableHead,
  balanceTransactionTableRow,
  balanceTransactionTableStyle,
} from '../../Table/Components/BalanceTransactionTable';
import {
  HoldingTableEmptyStyle,
  HoldingTableEmptyTextStyle,
} from '../../Table/Components/HoldingTable';

const BalanceTransactionsTable = ({
  data,
  accountUid,
  shouldShowTransactions,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { displayIRAEquityOptionsPermission } = useCheckPermissions();
  const settings = useSelector(selectSettings);
  const { brokerTheme } = config;
  const isGS = brokerTheme === 'GoldSilver';
  const isPriceAlertEligibleAccount = useSelector(
    selectIsPriceAlertEligibleAccount,
  );
  const hasGTTAccess =
    isGS && settings?.IsTriggerOrderActive && isPriceAlertEligibleAccount;

  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(
    false,
  );
  const [sfhTransactionUid, setSfhTransactionUid] = useState(null);
  const [isSfhHistoryView, setIsSfhHistoryView] = useState(false);
  const [isTriggerOrderDetailsOpen, setIsTriggerOrderDetailsOpen] = useState(
    false,
  );
  const [selectedTriggerOrder, setSelectedTriggerOrder] = useState(null);
  // Whether the transaction in TransactionDetails is one of a Target Price
  // order's rows (its reservation, say), which gets a Close button at the bottom.
  const [isTargetPriceOrderDetails, setIsTargetPriceOrderDetails] = useState(
    false,
  );

  const singleTransaction = useSelector(selectPortfolioSingleTransaction);

  const singleTransactionLoading = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );

  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );

  const [rowData, setRowData] = useState(null);

  useEffect(() => {
    if (data && data.length) {
      setRowData(
        data.map((transaction) => ({
          id: transaction.Uid,
          type: getSingleTransactionType(transaction),
          // The raw backend type, which Sell From Home rows are told apart by.
          rawType: transaction.Type,
          isTargetPriceOrder: isTargetPriceOrderTransaction(transaction),
          content: (
            <TransactionRow
              key={`${transaction.Uid}-${uuid()}`}
              month={formatDate(transaction.DateTime, 'MMM')}
              day={formatDate(transaction.DateTime, 'dd')}
              amount={transaction.Amount}
              balance={formatMoneyNumeral(transaction.EffectiveBalance)}
              status={parseEnumType(
                transactionStatusTypesArray,
                transaction.StatusType,
              )}
              action={
                transaction.Fields[0]
                  ? transactionFieldFormatter(
                      // A buy the BE flags as a Target Price Order reads as
                      // the order does (IsTriggerOrder).
                      transaction.IsTriggerOrder
                        ? asTargetPriceOrderSummary(transaction.Fields[0])
                        : transaction.Fields[0],
                      { isRow: true },
                    )
                  : null
              }
              product={
                transaction.Fields[1]
                  ? transactionFieldFormatter(transaction.Fields[1])
                  : null
              }
              isSegregated={transaction.IsSegregated}
              isTargetPriceOrder={isTargetPriceOrderTransaction(transaction)}
            />
          ),
        })),
      );
    }
  }, [data]); //eslint-disable-line

  const onTransactionCardClick = (transaction) => {
    const {
      id: transactionUid,
      type,
      rawType,
      isTargetPriceOrder,
    } = transaction;
    // Sell From Home rows open the dedicated SFH details modal, which fetches
    // the single transaction itself from the Uid it is handed. The account
    // transactions feed is unmapped, so Type arrives as the raw number (39);
    // also accept the mapped string in case a mapped feed is used.
    if (
      rawType === SELL_FROM_HOME ||
      rawType === transactionTypes.SellFromHome
    ) {
      setSfhTransactionUid(transactionUid);
      return;
    }

    if (type) {
      // The order opens in its own modal once it has loaded (the table's loader
      // shows meanwhile). The response lands in the store TransactionDetails
      // opens off, so make sure that one stays closed.
      setIsTransactionDetailsOpen(false);
      dispatch(
        portfolioTransactionsSingleFetch({
          accountUid,
          transactionUid,
          type,
          onSuccess: (details) => {
            setSelectedTriggerOrder(details);
            setIsTriggerOrderDetailsOpen(true);
          },
        }),
      );
      return;
    }

    dispatch(
      portfolioTransactionsSingleFetch({
        accountUid,
        transactionUid,
      }),
    );
    setIsTargetPriceOrderDetails(isTargetPriceOrder);
    setIsTransactionDetailsOpen(true);
  };

  const handleSfhDetailsClose = () => {
    dispatch(emptyPortfolioTransactionSingle());
    setSfhTransactionUid(null);
    setIsSfhHistoryView(false);
  };

  const handleSfhViewHistory = () => setIsSfhHistoryView(true);
  const handleSfhHistoryBack = () => setIsSfhHistoryView(false);

  const handleTriggerOrderDetailsClose = () => {
    setIsTriggerOrderDetailsOpen(false);
    dispatch(emptyPortfolioTransactionSingle());
  };

  if (isEmpty(rowData)) {
    return null;
  }

  return (
    <>
      <Table css={balanceTransactionTableStyle}>
        <TableHead
          data={
            displayIRAEquityOptionsPermission
              ? equityHeaderData
              : getHeaderData(hasGTTAccess)
          }
          css={balanceTransactionTableHead}
          displayIRAEquityOptionsPermission={displayIRAEquityOptionsPermission}
        />
        <SectionLoader isLoading={singleTransactionLoading}>
          {shouldShowTransactions ? (
            <TableBody>
              <TableRow
                key={uuid()}
                css={balanceTransactionTableRow}
                displayIRAEquityOptionsPermission={
                  displayIRAEquityOptionsPermission
                }
                hasArrow
                onClick={(transaction) => onTransactionCardClick(transaction)}
                rowData={rowData}
                data-cy="list-item-transaction"
              />
            </TableBody>
          ) : (
            <HoldingTableEmptyStyle>
              <HoldingTableEmptyTextStyle>
                {t('balances.noRecentTransactions')}
              </HoldingTableEmptyTextStyle>
            </HoldingTableEmptyStyle>
          )}
        </SectionLoader>
      </Table>
      <TransactionDetails
        isOpen={!isEmpty(singleTransaction) && isTransactionDetailsOpen}
        onClose={() => {
          dispatch(emptyPortfolioTransactionSingle());
          setIsTransactionDetailsOpen(false);
        }}
        transaction={singleTransaction}
        error={singleTransactionError}
        isTargetPriceOrder={isTargetPriceOrderDetails}
      />
      <TriggerOrderDetailsModal
        isOpen={isTriggerOrderDetailsOpen}
        onClose={handleTriggerOrderDetailsClose}
        transaction={selectedTriggerOrder}
        isEnded
      />
      <Modal
        isOpen={Boolean(sfhTransactionUid)}
        close={handleSfhDetailsClose}
        goBack={isSfhHistoryView ? handleSfhHistoryBack : undefined}
        title={
          <SfhModalTitle>
            {t(
              isSfhHistoryView
                ? 'sellWizard.sfhWizard.transactionHistory.title'
                : 'sellWizard.sfhWizard.transactionDetails.title',
            )}
          </SfhModalTitle>
        }
        headerBordered
        size="sm"
        maxWidth={pxToRem(640)}
        minHeight={pxToRem(640)}
        height={pxToRem(640)}
        clipContent
        closeIconVisible
        skipListeningToLocations
        dataCy="container-sfh-transaction-details"
      >
        {sfhTransactionUid &&
          (isSfhHistoryView ? (
            <SellFromHomeTransactionHistory
              lockedSnapshot={{ transactionUid: sfhTransactionUid }}
              handleClose={handleSfhDetailsClose}
            />
          ) : (
            <SellFromHomeTransactionDetails
              lockedSnapshot={{ transactionUid: sfhTransactionUid }}
              handleClose={handleSfhDetailsClose}
              handleViewHistory={handleSfhViewHistory}
              source="Financial Balances"
            />
          ))}
      </Modal>
    </>
  );
};

BalanceTransactionsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      DateTime: PropTypes.string,
      Amount: PropTypes.number,
      EffectiveBalance: PropTypes.number,
      StatusType: PropTypes.number,
      Type: PropTypes.number,
      Caption: PropTypes.string,
    }),
  ),
  accountUid: PropTypes.string,
  shouldShowTransactions: PropTypes.bool,
};

export default BalanceTransactionsTable;
